import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { PageTitle } from "../../../_metronic/layout/core";
import { FavoriteToggle } from "../../components/FavoriteToggle";
import { Map } from "../../components/Map";
import { Card } from "../../ui/Card";
import { Flex } from "../../ui/Flex";
import { Grid } from "../../ui/Grid";
import { Heading } from "../../ui/Heading";
import { Stack } from "../../ui/Stack";
import { Text } from "../../ui/Text";
import { AlertTriangle, Building, Mail, MapPin, Tag, TagIcon, TrendingUp, FileText, Briefcase, Target, Layers, DollarSign, Globe, Printer, Download, Copy } from "lucide-react";
import { getStore, setFavorite } from "../../api/copiloto-do-vendedor";
import { useNavigate, useParams } from "react-router-dom";
import { MapPoint } from "../../types/MapPoint";
import { RoutePath } from "../../routing/routes";
import { RawStoreCopilot } from "../../types/RawStoreCopilot";
import { RawStoreInfo } from "../../types/RawStoreInfo";
import { Table, TableRenderItemOptions } from "../../components/Table";
import { Badge } from "../../ui/Badge";
import { formatMoney, formatCNPJ, convertToBrazilianDate } from "../../utils/format";
import * as XLSX from "xlsx";
import { useReactToPrint } from 'react-to-print';

function rawStoreInfoToMapPoint(store: RawStoreInfo): MapPoint {
  return {
    guid: store.CNPJLojista.toString(),
    latitude: store.Latitude,
    longitude: store.Longitude,
    name: store.NomeFantasia,
    cnpj: formatCNPJ(store.CNPJLojista)
  };
}

export const CopilotoDoVendedorLojista = () => {
  // Define o tipo do ref para um elemento HTML
  const contentRef = useRef<HTMLDivElement>(null);
  const [store, setStore] = useState<RawStoreCopilot | undefined>(undefined);
  const navigate = useNavigate();
  const { cnpj } = useParams();

  const reactToPrintFn = useReactToPrint({ contentRef });

  const storeMapPoint = useMemo((): MapPoint | undefined => {
    if (!store?.info) return undefined;
    return rawStoreInfoToMapPoint(store.info);
  }, [store?.info]);

  const formatBeneficioTipo = (value:string) => {
    return value;
  }

  const nerbyMapPoints = useMemo((): MapPoint[] => {
    const res: MapPoint[] = [];
    store?.nearby?.forEach(s => {
      res.push(rawStoreInfoToMapPoint(s));
    });
    return res;
  }, [store]);

  const loadData = useCallback(async (cnpj: string) => {
    setStore(undefined);
    const res = await getStore(cnpj);
    if (!res.success) {
      return;
    }
    const storeData: any = res.data;
    storeData.churns.forEach((churn: any) => {
      churn.FlagChurnIndustria = churn.FlagChurnIndustria === 'S' ? 'SIM' : 'NÃO';
    });
    setStore(storeData);
  }, []);

  useEffect(() => {
    if (cnpj) loadData(cnpj);
  }, [loadData, cnpj]);

  const handleOnClickFavorite = useCallback((val: boolean) => {
    if (store) setFavorite(store.info.IDLojista, val);
  }, [store]);

  const handleClickMapPopup = useCallback((point: MapPoint) => {
    const path: RoutePath = '/copiloto-do-vendedor/lojista';
    navigate(`${path}/${point.guid}`);
  }, [navigate]);

  const handleCopyCoupon = useCallback((couponName: string) => {
    navigator.clipboard.writeText(couponName);
  }, []);

  const handleRenderItemPromotionTable = useCallback(({ item, key }: TableRenderItemOptions<RawStoreCopilot['promos'][0]>): React.ReactNode | undefined => {
    if (key === 'Nome') {
      return (
        <Flex gap="8px" alignItems="center">
          <TagIcon color='blue' size='16px' />
          <Text color='blue'>{item.Nome}</Text>
          <button
            onClick={() => handleCopyCoupon(item.Nome.replace('CA - Cupom ', ''))}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
            title="Copiar cupom"
          >
            <Copy size="16px" className="text-blue-500" />
          </button>
        </Flex>
      );
    } else if (key === 'BeneficioTipo') {
      return <Badge color='blue'>{formatBeneficioTipo(item.BeneficioTipo)}</Badge>;
    } else if (key === 'BeneficioValor') {
      return (
        <Flex>
          {item.BeneficioTipo === 'PER'
            ? <TagIcon color='blue' size='16px' />
            : <DollarSign color='blue' size='16px' />}
          <Text color='blue'>
            {item.BeneficioTipo === 'PER'
              ? parseFloat(item.BeneficioValor.toString()).toFixed(1) + ' %'
              : formatMoney(item.BeneficioValor)}
          </Text>
        </Flex>
      );
    }
    return undefined;
  }, [handleCopyCoupon]);

  const handleRenderItemChurnTable = useCallback(({ item, key }: TableRenderItemOptions<RawStoreCopilot['churns'][0]>): React.ReactNode | undefined => {
    if (key === 'NomeIndustria') {
      return <Text>{item.NomeIndustria}</Text>;
    } else if (key === 'FlagChurnIndustria') {
      return <Text>{item.FlagChurnIndustria}</Text>;
    } else if (key === 'DataUltimaCompraIndustria') {
      return <Text>{item.DataUltimaCompraIndustria}</Text>;
    } else if (key === 'CicloCompraDiasPorIndustria') {
      return <Text>{item.CicloCompraDiasPorIndustria}</Text>;
    }
    return undefined;
  }, []);

  const handleRenderItemFavoriteProductsTable = useCallback(({ item, key }: TableRenderItemOptions<RawStoreCopilot['favoriteProducts'][0]>): React.ReactNode | undefined => {
    return undefined;
  }, []);

  const handleRenderItemLastPurchaseTable = useCallback(({ item, key }: TableRenderItemOptions<RawStoreCopilot['lastPurchase'][0]>): React.ReactNode | undefined => {
    return undefined;
  }, []);

  const handleRenderItemVendaAITable = useCallback(({ item, key }: TableRenderItemOptions<RawStoreCopilot['vendaai'][0]>): React.ReactNode | undefined => {
    return undefined;
  }, []);

  const handleExportLastPurchase = useCallback(() => {
    if (!store || !store.lastPurchase) return;
    const exportData = store.lastPurchase.map(item => ({
      "Produto": item.NomeProduto,
      "Quantidade": item.Quantidade,
      "Preço Unitário": item.PrecoUnitarioSemDesconto,
      "Desconto": item.ValorDescontoUnitario,
      "Indústria": item.NomeIndustria
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Último Pedido");
    XLSX.writeFile(wb, `ultimo_pedido_${store.info.NomeFantasia}.xlsx`);
  }, [store]);

  return (
    <>
      <PageTitle>{"Copiloto do Vendedor > Lojista"}</PageTitle>
      <div className="flex justify-end gap-4 mb-6">
        <button
          // Verifica se o conteúdo a ser impresso está disponível antes de chamar a função
          onClick={() => {
            reactToPrintFn()
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 text-gray-700"
          title="Imprimir página"
        >
          <Printer size="18px" />
          <span>Imprimir</span>
        </button>
      </div>
      {/*
         Encapsula o conteúdo a ser impresso em uma div que recebe o ref.
         Assim garantimos que o elemento referenciado seja um nó DOM.
      */}
      <div ref={contentRef}>
        <Stack gap="16px" margin="0 0 16px 0">
          {store && (
            <>
              <Card>
                <Stack>
                  <Flex justifyContent="space-between">
                    <Heading>{store.info.NomeFantasia}</Heading>
                    <FavoriteToggle isFavorite={!!store.info.is_favorite} onClick={handleOnClickFavorite} />
                  </Flex>
                  <Grid columns="1fr 1fr">
                    <Flex>
                      <FileText size="16px" />
                      <Text>Nome Fantasia: {store.info.NomeFantasia}</Text>
                    </Flex>
                    <Flex>
                      <Briefcase size="16px" />
                      <Text>Razão Social: {store.info.RazaoSocial}</Text>
                    </Flex>
                    <Flex>
                      <Building size="16px" />
                      <Text>CNPJ: {formatCNPJ(store.info.CNPJLojista)}</Text>
                    </Flex>
                    <Flex>
                      <Mail size="16px" />
                      <Text>{store.info.Email}</Text>
                    </Flex>
                    <Flex>
                      <Globe size="16px" />
                      <Text>Região: {store.info.Regiao}</Text>
                    </Flex>
                    <Flex>
                      <Layers size="16px" />
                      <Text>Segmento: {store.info.Segmento}</Text>
                    </Flex>
                    <Flex>
                      <DollarSign size="16px" />
                      <Text>Pagamento Predominante: {store.info.FormaPagamentoPredominante}</Text>
                    </Flex>
                    <Flex>
                      <MapPin size="16px" />
                      <Text>{store.info.Cidade} / {store.info.Estado}</Text>
                    </Flex>
                    <Flex>
                      <DollarSign size="16px" />
                      <Text>Limite Crédito: {formatMoney(store.info.LimiteCredito)}</Text>
                    </Flex>
                    <Flex>
                      <DollarSign size="16px" />
                      <Text>Limite Saldo: {formatMoney(store.info.LimiteSaldo)}</Text>
                    </Flex>
                    <Flex>
                      <DollarSign size="16px" />
                      <Text>Limite Utilizado: {formatMoney(store.info.LimiteUtilizado)}</Text>
                    </Flex>
                  </Grid>
                </Stack>
              </Card>
            </>
          )}

          {store && store.vendaai.length ? (
            <Card>
              <Stack>
                <Flex>
                  <Target size="16px" />
                  <Heading>Recomendação - Venda AI</Heading>
                </Flex>
                <Table
                  schema={[
                    { key: 'DataReferencia', label: 'Data da Predição' },
                    { key: 'PredicaoSellOut', label: 'Potencial de Compra desta loja' },
                  ]}
                  useRenderItem={false}
                  data={store.vendaai}
                  formats={[
                    { key: 'DataReferencia', type: 'date' },
                    { key: 'PredicaoSellOut', type: 'money' },
                  ]}
                  onRenderItem={handleRenderItemVendaAITable}
                />
              </Stack>
            </Card>
          ) : null}

          {store && (
            <Card>
              <Stack>
                <Flex>
                  <TrendingUp size="16px" />
                  <Heading>Oportunidades de Venda</Heading>
                </Flex>
                <Table
                  schema={[
                    { key: 'NomeProduto', label: 'Produto' },
                    { key: 'TotalQuantidade', label: 'Número de Pedidos' }
                  ]}
                  data={store.favoriteProducts}
                  onRenderItem={handleRenderItemFavoriteProductsTable}
                  align="center"
                  useRenderItem={false}
                />
              </Stack>
            </Card>
          )}

          {store && (
            <Card>
              <Stack>
                <Flex>
                  <Tag size="16px" />
                  <Heading>Recomendações de Cupons</Heading>
                </Flex>
                {store.promos.length ? (
                  <Table
                    schema={[
                      { key: 'Nome', label: 'Cupom' },
                      { key: 'BeneficioTipo', label: 'Tipo' },
                      { key: 'BeneficioValor', label: 'Valor' },
                    ]}
                    data={store.promos}
                    onRenderItem={handleRenderItemPromotionTable}
                  />
                ) : (
                  <p>Nenhuma promoção encontrada para esse lojista.</p>
                )}
              </Stack>
            </Card>
          )}

          {store && store.churns.length ? (
            <Card>
              <Stack>
                <Flex>
                  <AlertTriangle size="16px" />
                  <Heading>Churn por Indústrias</Heading>
                </Flex>
                <Table
                  schema={[
                    { key: 'NomeIndustria', label: 'Indústria' },
                    { key: 'FlagChurnIndustria', label: 'Churn' },
                    { key: 'TicketMedioPedidoPorIndustria', label: 'Ticket Médio por Pedido' },
                    { key: 'DataUltimaCompraIndustria', label: 'Data Última compra' },
                  ]}
                  data={store.churns}
                  onRenderItem={handleRenderItemChurnTable}
                  align="center"
                  useRenderItem={false}
                  formats={[
                    { key: 'TicketMedioPedidoPorIndustria', type: 'money' },
                    { key: 'DataUltimaCompraIndustria', type: 'datetime' },
                  ]}
                  rowStyles={[
                    { key: 'FlagChurnIndustria', value: 'NÃO', rowClassName: 'bg-green-100' },
                  ]}
                />
              </Stack>
            </Card>
          ) : null}

          {store && store.lastPurchase?.length ? (
            <Card>
              <Stack>
                <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center" gap="2">
                    <TrendingUp size="16px" />
                    <Heading>
                      Último pedido realizado - {convertToBrazilianDate(store.lastPurchase[0].DataPedido)}
                    </Heading>
                  </Flex>
                  <button
                    onClick={handleExportLastPurchase}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 text-gray-700"
                    title="Exportar último pedido"
                  >
                    <Download size="18px" />
                    <span>Exportar</span>
                  </button>
                </Flex>
                <Table
                  schema={[
                    { key: 'NomeProduto', label: 'Produto' },
                    { key: 'Quantidade', label: 'Quantidade' },
                    { key: 'PrecoUnitarioSemDesconto', label: 'Preço Unitário' },
                    { key: 'ValorDescontoUnitario', label: 'Desconto' },
                    { key: 'NomeIndustria', label: 'Indústria' }
                  ]}
                  formats={[
                    { key: 'PrecoUnitarioSemDesconto', type: 'money' },
                    { key: 'ValorDescontoUnitario', type: 'money' },
                  ]}
                  data={store.lastPurchase}
                  onRenderItem={handleRenderItemLastPurchaseTable}
                  align="center"
                  useRenderItem={false}
                />
              </Stack>
            </Card>
          ) : null}

          <Card>
            <Stack>
              <Flex>
                <MapPin size="16px" />
                <Heading>Lojas Próximas</Heading>
              </Flex>
              <Map
                width="100%"
                height="400px"
                main={storeMapPoint}
                points={nerbyMapPoints}
                onClickPopup={handleClickMapPopup}
              />
            </Stack>
          </Card>
        </Stack>
      </div>
    </>
  );
};

export default CopilotoDoVendedorLojista;
